import React, { createContext, useContext, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const AudioPlayerContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  
  const audioRef = useRef(new Audio());

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.pause();
      audio.src = '';
    };
    // Intentionally empty deps - setup audio element listeners only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    audioRef.current.muted = isMuted;
  }, [isMuted]);

  const recordTrackPlay = useCallback(async (trackId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/analytics/track-play/${trackId}`);
    } catch (error) {
      // Analytics failure shouldn't disrupt playback
    }
  }, []);

  const playTrack = useCallback((track, epInfo = null) => {
    const audio = audioRef.current;
    
    // If it's the same track, just toggle play/pause
    if (currentTrack?._id === track._id) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().then(() => {
          // Record play when resuming
          recordTrackPlay(track._id);
        }).catch(err => {
          // Ignore AbortError (happens when user switches tracks quickly)
          if (err.name !== 'AbortError') {
            console.error('Play error:', err);
          }
          setIsPlaying(false);
        });
      }
      return;
    }

    // Stop current track and play new one
    audio.pause();
    audio.currentTime = 0;
    
    setCurrentTrack({ ...track, epInfo });
    setCurrentTime(0);
    setDuration(0);
    
    if (track.audio_url) {
      const audioUrl = `${BACKEND_URL}${track.audio_url}`;
      
      audio.src = audioUrl;
      audio.load();
      
      // Add error handling for audio loading
      audio.onerror = () => {
        setIsPlaying(false);
        alert('Failed to load audio file. Please check if the file exists.');
      };
      
      audio.play().then(() => {
        // Record play when starting new track
        recordTrackPlay(track._id);
      }).catch(err => {
        // Ignore AbortError (happens when user switches tracks quickly)
        if (err.name !== 'AbortError') {
          setIsPlaying(false);
        }
      });
    } else {
      setIsPlaying(false);
    }
  }, [currentTrack, isPlaying, recordTrackPlay]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, [isPlaying]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  }, []);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const changeVolume = useCallback((newVolume) => {
    setVolume(newVolume);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Context value with all player state and controls
  // Many dependencies are necessary - this is the public API for the audio player
  const contextValue = useMemo(() => ({
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playTrack,
    togglePlay,
    stop,
    seek,
    changeVolume,
    toggleMute
  }), [currentTrack, isPlaying, currentTime, duration, volume, isMuted, playTrack, togglePlay, stop, seek, changeVolume, toggleMute]);

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
    </AudioPlayerContext.Provider>
  );
};
